// Acceptable rounding difference for floating-point values from OCR/extraction pipelines
const TOLERANCE = 0.01;

export interface LineItem {
  [key: string]: unknown;
  quantity?: number | string | null;
  qty?: number | string | null;
  unit_price?: number | string | null;
  total?: number | string | null;
  line_number?: number | string | null;
}

export interface MismatchCheckInput {
  lineItems: LineItem[];
  subtotal: number | string | null | undefined;
  docTotal: number | string | null | undefined;
  taxTotal: number | string | null | undefined;
  discountTotal: number | string | null | undefined;
  additionalCharges: number | string | null | undefined;
}

function toNum(v: unknown): number {
  if (v === null || v === undefined || v === '') return 0;
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  return isNaN(n) ? 0 : n;
}

// Formats as USD; sufficient for the current single-currency implementation
function fmt(n: number): string {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

/**
 * Returns an array of human-readable issue strings.
 * Empty array means no mismatches detected.
 */
export function detectLineMismatches(input: MismatchCheckInput): string[] {
  const issues: string[] = [];

  // Check 1: per-line qty × unit_price ≠ line.total
  input.lineItems.forEach((line, idx) => {
    const qty = toNum(line.quantity ?? line.qty);
    const unitPrice = toNum(line.unit_price);
    const lineTotal = toNum(line.total);
    const lineLabel = `Line ${line.line_number ?? idx + 1}`;

    // Only check when both qty and unit_price are non-zero
    if (qty !== 0 && unitPrice !== 0) {
      const expected = qty * unitPrice;
      if (Math.abs(expected - lineTotal) > TOLERANCE) {
        issues.push(
          `${lineLabel}: ${qty.toLocaleString('en-US')} × ${fmt(unitPrice)} = ${fmt(expected)}, but line total shows ${fmt(lineTotal)}`
        );
      }
    }
  });

  // Check 2: sum of line totals + tax + additional_charges - discount ≠ doc total
  const docTotal = toNum(input.docTotal);
  if (docTotal !== 0 && input.lineItems.length > 0) {
    const lineSum = input.lineItems.reduce((acc, line) => acc + toNum(line.total), 0);
    const tax = toNum(input.taxTotal);
    const discount = toNum(input.discountTotal);
    const additional = toNum(input.additionalCharges);
    const expected = lineSum + tax + additional - discount;

    if (Math.abs(expected - docTotal) > TOLERANCE) {
      const parts: string[] = [`Line items (${fmt(lineSum)})`];
      if (tax !== 0) parts.push(`+ tax (${fmt(tax)})`);
      if (additional !== 0) parts.push(`+ additional charges (${fmt(additional)})`);
      if (discount !== 0) parts.push(`− discount (${fmt(discount)})`);
      issues.push(`${parts.join(' ')} = ${fmt(expected)}, but document total is ${fmt(docTotal)}`);
    }
  }

  // Check 3: header-level subtotal + tax ≠ doc total
  if (docTotal !== 0) {
    const subtotal = toNum(input.subtotal);
    const tax = toNum(input.taxTotal);
    const expectedHeaderTotal = subtotal + tax;

    if (Math.abs(expectedHeaderTotal - docTotal) > TOLERANCE) {
      issues.push(
        `Subtotal (${fmt(subtotal)}) + tax (${fmt(tax)}) = ${fmt(expectedHeaderTotal)}, but document total is ${fmt(docTotal)}`
      );
    }
  }

  return issues;
}
